/**
 * The folded event log `deriveRunView` reads from: every event once, in seq order, with
 * times measured from the run's start.
 *
 * @since 1.0.0
 */
import { Option } from "effect";
import type { RunDetail } from "../api.ts";
import type { RunEvent } from "../model.ts";

/**
 * The event of one `kind`.
 *
 * @since 1.0.0
 */
export type EventOf<K extends RunEvent["kind"]> = Extract<RunEvent, { readonly kind: K }>;

/**
 * @since 1.0.0
 */
export interface Timeline {
  /** Stored then streamed, each seq once, oldest first. */
  readonly events: ReadonlyArray<RunEvent>;
  readonly started: Option.Option<EventOf<"run.started">>;
  readonly finished: Option.Option<EventOf<"run.finished">>;
  /** True until `run.finished`. */
  readonly live: boolean;
  /** The run's start as epoch milliseconds: time zero for every other time here. */
  readonly originMs: number;
  /** Milliseconds from the run's start to an event. */
  readonly ms: (event: { readonly at: string }) => number;
  /** Now, from the run's start. */
  readonly nowMs: number;
  /** When `run.finished` happened, from the run's start; absent while live. */
  readonly endMs: number | undefined;
}

/**
 * The events of one kind, narrowed.
 *
 * @since 1.0.0
 */
export const ofKind = <K extends RunEvent["kind"]>(
  events: ReadonlyArray<RunEvent>,
  kind: K,
): ReadonlyArray<EventOf<K>> => events.filter((e): e is EventOf<K> => e.kind === kind);

/**
 * The story key of the item an event becomes.
 *
 * @since 1.0.0
 */
export const storyKey = (event: RunEvent): string => `e${event.seq}`;

const merge = (stored: ReadonlyArray<RunEvent>, streamed: ReadonlyArray<RunEvent>): ReadonlyArray<RunEvent> =>
  [...new Map([...stored, ...streamed].reverse().map((e) => [e.seq, e] as const)).values()]
    .sort((a, b) => a.seq - b.seq);

/**
 * Fold a run's stored and streamed events into a timeline. Time zero is `run.started`,
 * else the row's start, else its creation — a dispatch that failed before `run.started`
 * still has a clock.
 *
 * @since 1.0.0
 */
export const timeline = (detail: RunDetail, streamed: ReadonlyArray<RunEvent>, nowMs: number): Timeline => {
  const events = merge(detail.events, streamed);
  const started = Option.fromNullable(ofKind(events, "run.started").at(0));
  const finished = Option.fromNullable(ofKind(events, "run.finished").at(-1));
  const t0 = Date.parse(
    Option.match(started, { onNone: () => detail.run.startedAt ?? detail.run.createdAt, onSome: (s) => s.at }),
  );
  const ms = (event: { readonly at: string }) => Date.parse(event.at) - t0;
  return {
    events,
    started,
    finished,
    live: Option.isNone(finished),
    originMs: t0,
    ms,
    nowMs: nowMs - t0,
    endMs: Option.getOrUndefined(Option.map(finished, ms)),
  };
};
